import { LinearGradient } from 'expo-linear-gradient';
import React, { useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Loading from '../components/common/Loading';
import { LabelType } from '../components/common/Label';
import { useAppSelector } from '../hooks/store-hook';
import { CommonActions, useFocusEffect } from '@react-navigation/native';
import { LoggedState } from '../models/AuthModelsCommon';

const EntryPage = ({ route, routes, navigation }) => {
  const authStore = useAppSelector((selector) => selector.auth);
  const navBarStore = useAppSelector((selector) => selector.navBar);
  const insets = useSafeAreaInsets();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useFocusEffect(
    React.useCallback(() => {
      if (navBarStore.isWebView) {
        //@ts-ignore
        window.ReactNativeWebView.postMessage('error');
      } else {
        // Avoid flicker caused by multiple timers when auth state changes during app bootstrap.
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }

        // Auth can take a moment to resolve on cold start. If it never resolves we must not hang forever:
        // fall back to login after a short grace period.
        const authResolved = authStore.logged !== undefined;
        const isAuthed = authStore.logged === LoggedState.Anonymous || authStore.logged === LoggedState.Logged;
        // `currentPath` reflects the currently displayed screen (often `entryPage` at boot).
        // For bootstrap routing we want the intended target (`path`, defaults to `map`).
        const nextAuthedRoute = navBarStore.path && navBarStore.path !== 'entryPage' ? navBarStore.path : 'map';
        const nextRoute = authResolved && isAuthed ? nextAuthedRoute : 'login';
        const nextParams = nextRoute === 'login' ? (route?.params as any) : undefined;
        const delayMs = authResolved ? 150 : 2500;

        timerRef.current = setTimeout(() => {
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [{ name: nextRoute as never, params: nextParams } as never],
            }),
          );
        }, delayMs);
      }

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }, [authStore.logged, navBarStore.isWebView, navBarStore.path, route?.params, navigation])
  );

  return (
    <View style={styles.root}>
      {/* This screen is rendered inside the app SafeAreaView. Extend the background into the inset areas. */}
      <LinearGradient
        pointerEvents="none"
        end={{ x: 0.1, y: 0.4 }}
        style={[StyleSheet.absoluteFillObject, { top: -insets.top, bottom: -insets.bottom }]}
        colors={['#022d38', '#034050', '#044c5e', '#04647c', '#43a49e']}
      />
      <Loading textVariant={LabelType.loginLoading} iconColor={'white'} />
    </View>
  );
};

export default EntryPage;

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
});
